/* POVA — motion + interaction layer
 *
 * Wave-1 ship list:
 *  1. Pointer-tracked --mx/--my on hoverable elements (cards + CTAs)
 *  2. Touch-tap pulses (.is-pulsing) for non-hover devices
 *  3. Custom cursor (orange dot + delayed outline ring), fine-pointer only
 *  4. Type scrub-in (split + per-char animation-delay)
 *  5. Subtle scroll parallax on .scroll-parallax
 *  6. Reveal-on-scroll (.reveal -> .is-in) via IntersectionObserver
 *  7. Schematic chapter reveal (.schematic-chapter -> .is-in)
 *  8. Halo color on focus (keyboard parity with hover)
 *  9. Touch fallback for product .instrument labels
 * 10. Touch-finger field halo (mobile parity for cursor halo)
 *
 * Loads as a regular script (defer in HTML). All work is gated on
 *   prefers-reduced-motion: reduce — that branch collapses to fades / no-ops.
 */

(function () {
  "use strict";

  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isFinePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;

  /* ---------------------------------------------------------------------
   * 0. Hamburger nav (mobile) — top-RIGHT standalone glass button.
   *    Cart pill stays top-left. Drawer slides in from the right.
   *    Hidden on desktop via CSS. Toggles `body.nav-open`.
   * ------------------------------------------------------------------- */
  if (!document.querySelector(".hamburger-btn")) {
    const burger = document.createElement("button");
    burger.type = "button";
    burger.className = "glass hamburger-btn";
    burger.setAttribute("aria-label", "Open menu");
    burger.setAttribute("aria-expanded", "false");
    burger.innerHTML = '<span class="bars" aria-hidden="true"><span></span><span></span><span></span></span>';
    document.body.appendChild(burger);

    /* Backdrop — sits behind the drawer, dims the page, catches the
     * click-outside-to-dismiss gesture so the user always has a way out. */
    const backdrop = document.createElement("div");
    backdrop.className = "nav-backdrop";
    backdrop.setAttribute("aria-hidden", "true");
    document.body.appendChild(backdrop);

    const drawer = document.createElement("nav");
    drawer.className = "nav-drawer";
    drawer.setAttribute("aria-label", "Primary menu");
    /* Root-relative paths so the drawer works from every page — including
     * /product/pova-8.html, where bare "/" would resolve to
     * /product/index.html and 404. Compute a base prefix dynamically so
     * the same JS works whether the site is served at "/" or under a
     * subpath in dev. */
    const basePrefix = "/";  /* Shopify serves every page from the root */
    drawer.innerHTML = `
      <button type="button" class="nav-drawer-close" aria-label="Close menu"></button>
      <ul>
        <li><a href="${basePrefix}">Home</a></li>
        <li><a href="${basePrefix}pages/products">Phones</a></li>
        <li><a href="${basePrefix}collections/audio">Audio</a></li>
        <li><a href="${basePrefix}pages/shop">Shop All</a></li>
        <li><a href="${basePrefix}pages/blog">Field Notes</a></li>
      </ul>
      <div class="nav-drawer-sub">
        <a href="${basePrefix}search">Search</a>
        <a href="${basePrefix}account">Account</a>
        <a href="${basePrefix}pages/support">Support</a>
        <a href="${basePrefix}pages/stores">Find Store</a>
        <a href="${basePrefix}pages/about">About</a>
      </div>
    `;
    document.body.appendChild(drawer);

    function toggleNav(open) {
      const isOpen = open === undefined ? !document.body.classList.contains("nav-open") : open;
      document.body.classList.toggle("nav-open", isOpen);
      burger.setAttribute("aria-expanded", isOpen ? "true" : "false");
      burger.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
    }
    burger.addEventListener("click", () => toggleNav());
    /* Visible × inside the drawer */
    drawer.querySelector(".nav-drawer-close").addEventListener("click", () => toggleNav(false));
    /* Click backdrop → close */
    backdrop.addEventListener("click", () => toggleNav(false));
    /* Tap any link in the drawer → close */
    drawer.querySelectorAll("a").forEach((a) => {
      a.addEventListener("click", () => toggleNav(false));
    });
    /* Esc to close */
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && document.body.classList.contains("nav-open")) toggleNav(false);
    });
  }

  /* ---------------------------------------------------------------------
   * 1 + 2. Pointer-tracked --mx / --my on .grad-hover and card families.
   * Apply to all the existing card families without changing their markup.
   * ------------------------------------------------------------------- */
  const HOVER_TARGETS = [
    ".grad-hover",
    ".product-card",
    ".note-card",
    ".value-card",
    ".retailer-card",
    ".contact-row",
    ".gallery-plate",
    ".buy-bar .cta",
    ".about-cta .cta",
    ".cta-link"
  ].join(",");

  function setLocalPointer(el, e) {
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.setProperty("--mx", (x / rect.width) * 100 + "%");
    el.style.setProperty("--my", (y / rect.height) * 100 + "%");
  }

  /* Reduced motion (WCAG 2.1 §2.3.3): this is a continuous pointer-tracked
   * gradient-hover sweep, purely decorative. Gate the ambient listener the
   * same way js-field.js gates its cursor-halo loop — the touch tap-pulse
   * handler below still calls setLocalPointer() on a discrete tap, which is
   * a one-shot position set, not a continuous animation, so it is left as-is. */
  if (!reduceMotion) {
    document.addEventListener("pointermove", (e) => {
      const el = e.target.closest(HOVER_TARGETS);
      if (el) setLocalPointer(el, e);
    });
  }

  /* Inject a .card-glow child into every card-family element so the
   * gradient overlay can sit above the card background but below content,
   * without fighting the existing ::before/::after corner-mark pseudos. */
  const CARD_FAMILIES = [
    ".product-card",
    ".note-card",
    ".value-card",
    ".retailer-card",
    ".contact-row"
  ];
  CARD_FAMILIES.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      if (el.querySelector(":scope > .card-glow")) return;
      const glow = document.createElement("span");
      glow.className = "card-glow";
      glow.setAttribute("aria-hidden", "true");
      el.prepend(glow);
    });
  });

  /* Touch tap pulse — fires .is-pulsing for one paint when an interactive
   * card / CTA is touch-tapped. Removes after the animation. */
  if (isTouch) {
    document.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "touch") return;
      const el = e.target.closest(HOVER_TARGETS);
      if (!el) return;
      setLocalPointer(el, e);
      el.classList.add("is-pulsing");
      setTimeout(() => el.classList.remove("is-pulsing"), 650);
      /* Mark the card as tapped for the touch hover-style state */
      const tappable = e.target.closest(".product-card, .note-card, .value-card, .retailer-card, .instrument");
      if (tappable) {
        document.querySelectorAll(".is-tapped").forEach((p) => {
          if (p !== tappable) p.classList.remove("is-tapped");
        });
        tappable.classList.add("is-tapped");
      }
    });
  }

  /* ---------------------------------------------------------------------
   * 3. Custom cursor — fine-pointer only, hidden on touch / reduced-motion.
   * ------------------------------------------------------------------- */
  if (isFinePointer && !reduceMotion) {
    const dot = document.createElement("div");
    dot.className = "cursor-dot";
    dot.setAttribute("aria-hidden", "true");
    const ring = document.createElement("div");
    ring.className = "cursor-ring";
    ring.setAttribute("aria-hidden", "true");
    document.body.append(dot, ring);

    let dx = window.innerWidth / 2;
    let dy = window.innerHeight / 2;
    let rx = dx;
    let ry = dy;

    window.addEventListener("pointermove", (e) => {
      dx = e.clientX;
      dy = e.clientY;
      if (!document.body.classList.contains("cursor-ready")) {
        document.body.classList.add("cursor-ready");
      }
    }, { passive: true });

    window.addEventListener("pointerleave", () => {
      document.body.classList.remove("cursor-ready");
    });

    /* Hover-state morph */
    const HOVER_CURSOR_TARGETS = "a, button, [role='button'], [data-product], .swatch, .thumb, .skip-link, .glass.cart";
    document.addEventListener("pointerover", (e) => {
      if (e.target.closest && e.target.closest(HOVER_CURSOR_TARGETS)) {
        document.body.classList.add("cursor-hover");
      }
    });
    document.addEventListener("pointerout", (e) => {
      if (e.target.closest && e.target.closest(HOVER_CURSOR_TARGETS)) {
        const to = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(HOVER_CURSOR_TARGETS);
        if (!to) document.body.classList.remove("cursor-hover");
      }
    });
    document.addEventListener("pointerdown", () => document.body.classList.add("cursor-pressed"));
    document.addEventListener("pointerup",   () => document.body.classList.remove("cursor-pressed"));

    function tickCursor() {
      /* dot follows pointer 1:1 */
      dot.style.transform = `translate3d(${dx}px, ${dy}px, 0) translate(-50%, -50%)`;
      /* ring lerps for a slight delayed-follow */
      rx += (dx - rx) * 0.18;
      ry += (dy - ry) * 0.18;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%)`;
      requestAnimationFrame(tickCursor);
    }
    requestAnimationFrame(tickCursor);
  }

  /* ---------------------------------------------------------------------
   * 4. Type scrub-in. Apply to .scrub-in elements — split inner text into
   *    chars, set --i so each char's animation-delay scales.
   * ------------------------------------------------------------------- */
  function scrubInify(el) {
    if (el.dataset.scrubReady) return;
    el.dataset.scrubReady = "1";
    const text = el.textContent;
    el.textContent = "";
    let charIndex = 0;
    text.split(/(\s+)/).forEach((word) => {
      if (/^\s+$/.test(word)) {
        el.appendChild(document.createTextNode(word));
        return;
      }
      const wordEl = document.createElement("span");
      wordEl.className = "word";
      [...word].forEach((ch) => {
        const c = document.createElement("span");
        c.className = "char";
        c.style.setProperty("--i", charIndex++);
        c.textContent = ch;
        wordEl.appendChild(c);
      });
      el.appendChild(wordEl);
    });
  }
  document.querySelectorAll(".scrub-in").forEach(scrubInify);

  /* ---------------------------------------------------------------------
   * 5. Scroll parallax. Subtle 8% drift on .scroll-parallax elements.
   * ------------------------------------------------------------------- */
  if (!reduceMotion) {
    const parallaxEls = [...document.querySelectorAll(".scroll-parallax")];
    if (parallaxEls.length) {
      let lastScroll = window.scrollY;
      let ticking = false;
      function applyParallax() {
        const vh = window.innerHeight;
        parallaxEls.forEach((el) => {
          const rect = el.getBoundingClientRect();
          /* progress = 0 when element is at viewport bottom, 1 at top */
          const progress = 1 - Math.min(Math.max((rect.top + rect.height / 2) / vh, 0), 1);
          const drift = (progress - 0.5) * (parseFloat(el.dataset.parallax) || 30);
          el.style.setProperty("--parallax-y", drift + "px");
        });
        ticking = false;
      }
      window.addEventListener("scroll", () => {
        lastScroll = window.scrollY;
        if (!ticking) {
          requestAnimationFrame(applyParallax);
          ticking = true;
        }
      }, { passive: true });
      applyParallax();
    }
  }

  /* ---------------------------------------------------------------------
   * 6. Reveal-on-scroll. Generic .reveal -> .is-in via IntersectionObserver.
   * Also handles .spec-row, .masked-reveal, and .schematic-chapter.
   * ------------------------------------------------------------------- */
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.1 });

    document.querySelectorAll(".reveal, .spec-row").forEach((el) => io.observe(el));

    /* 6a. Footer enter/leave — toggles body.in-footer so chrome elements
     *     (ticker, context-card, custom cursor) can fade out / dim when
     *     the footer is on screen. Prevents the rotated ticker on the
     *     left edge from bleeding through footer copy. */
    const footerEl = document.querySelector(".footer");
    if (footerEl) {
      const footerIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          document.body.classList.toggle("in-footer", entry.isIntersecting);
        });
      }, { threshold: 0.05 });
      footerIO.observe(footerEl);
    }

    /* 6b. Masked-reveal (display lockups) — separate threshold so the mask
     *     can scrub in only when the headline is comfortably in view. */
    const maskedIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          maskedIO.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -15% 0px", threshold: 0.2 });
    document.querySelectorAll(".masked-reveal").forEach((el) => maskedIO.observe(el));

    /* 7. Schematic chapter reveal — "active chapter wins" model. The
     *    chapter whose midpoint is closest to the viewport center gets
     *    .is-in; every other chapter loses it. Prevents the wave-2 bug
     *    where all three chapters ended up co-active with stacked z. */
    const chapters = [...document.querySelectorAll(".schematic-chapter")];
    if (chapters.length) {
      let lastActive = null;
      function updateActiveChapter() {
        const mid = window.innerHeight / 2;
        let bestEl = null;
        let bestDist = Infinity;
        chapters.forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) return;
          const elMid = r.top + r.height / 2;
          const dist = Math.abs(elMid - mid);
          if (dist < bestDist) { bestDist = dist; bestEl = el; }
        });
        if (bestEl !== lastActive) {
          chapters.forEach((c) => c.classList.toggle("is-in", c === bestEl));
          lastActive = bestEl;
        }
      }
      let chapterTicking = false;
      function onScrollChapter() {
        if (chapterTicking) return;
        chapterTicking = true;
        requestAnimationFrame(() => { updateActiveChapter(); chapterTicking = false; });
      }
      window.addEventListener("scroll", onScrollChapter, { passive: true });
      window.addEventListener("resize", onScrollChapter);
      updateActiveChapter();
    }

    /* Generic chaptered scroll narrative for non-POVA-8 PDPs.
     * Same active-wins logic, different selector — keeps each PDP's
     * narrative scroll choreography in sync without per-page JS. */
    const narrativeChapters = [...document.querySelectorAll("[data-narrative-chapter]")];
    if (narrativeChapters.length) {
      let nLast = null;
      function updateNarrative() {
        const mid = window.innerHeight / 2;
        let bestEl = null;
        let bestDist = Infinity;
        narrativeChapters.forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) return;
          const elMid = r.top + r.height / 2;
          const dist = Math.abs(elMid - mid);
          if (dist < bestDist) { bestDist = dist; bestEl = el; }
        });
        if (bestEl !== nLast) {
          narrativeChapters.forEach((c) => c.classList.toggle("is-in", c === bestEl));
          nLast = bestEl;
        }
      }
      let nTicking = false;
      function onScrollN() {
        if (nTicking) return;
        nTicking = true;
        requestAnimationFrame(() => { updateNarrative(); nTicking = false; });
      }
      window.addEventListener("scroll", onScrollN, { passive: true });
      window.addEventListener("resize", onScrollN);
      updateNarrative();
    }
  }

  /* ---------------------------------------------------------------------
   * 7b. Swatch radio-group arrow-key navigation (WCAG 2.1).
   *     Every [role="radiogroup"] containing [role="radio"] gets full
   *     arrow / home / end navigation. Aria-checked is the source of truth.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[role="radiogroup"]').forEach((group) => {
    const radios = [...group.querySelectorAll('[role="radio"]')];
    if (!radios.length) return;
    /* Initial tabindex: only the checked one is in tab order. */
    function syncTabindex() {
      const idx = radios.findIndex((r) => r.getAttribute("aria-checked") === "true");
      radios.forEach((r, i) => r.setAttribute("tabindex", i === idx || (idx === -1 && i === 0) ? "0" : "-1"));
    }
    syncTabindex();
    group.addEventListener("keydown", (e) => {
      const i = radios.indexOf(document.activeElement);
      if (i === -1) return;
      let next = -1;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % radios.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + radios.length) % radios.length;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = radios.length - 1;
      else if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        radios[i].click();
        return;
      }
      if (next === -1) return;
      e.preventDefault();
      radios[next].focus();
      radios[next].click();
    });
    /* Keep tabindex synced when aria-checked changes via click. */
    const obs = new MutationObserver(syncTabindex);
    radios.forEach((r) => obs.observe(r, { attributes: true, attributeFilter: ["aria-checked"] }));
  });

  /* ---------------------------------------------------------------------
   * 7c. Mobile context-card collapse — tap to expand on small screens.
   *     The CSS handles max-height; JS toggles .is-open and aria-expanded.
   *     Role/tabindex are GATED on the mobile breakpoint so desktop users
   *     do not get a phantom button tab stop on the (always-open) card.
   * ------------------------------------------------------------------- */
  const ctx = document.querySelector(".context-card");
  if (ctx) {
    const mqMobile = window.matchMedia("(max-width: 600px)");
    function toggleCtx(open) {
      const isOpen = open === undefined ? !ctx.classList.contains("is-open") : open;
      ctx.classList.toggle("is-open", isOpen);
      document.body.classList.toggle("ctx-open", isOpen);
      ctx.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }
    /* Inject the mobile-only "+ Details" trigger pill + close button
     * once on first run. Pill stays fixed bottom-left above the buy bar;
     * × close lives inside the card. Desktop CSS hides both. */
    if (!document.querySelector(".pdp-info-trigger")) {
      const trigger = document.createElement("button");
      trigger.className = "pdp-info-trigger";
      trigger.type = "button";
      trigger.setAttribute("aria-label", "Show product details");
      trigger.innerHTML = '<span class="icon" aria-hidden="true">+</span> Details';
      document.body.appendChild(trigger);
      trigger.addEventListener("click", () => toggleCtx(true));
    }
    if (!ctx.querySelector(".ctx-close")) {
      const closeBtn = document.createElement("button");
      closeBtn.className = "ctx-close";
      closeBtn.type = "button";
      closeBtn.setAttribute("aria-label", "Close details");
      closeBtn.innerHTML = "×";
      ctx.appendChild(closeBtn);
      closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleCtx(false);
      });
    }
    function applyMobileRole() {
      if (mqMobile.matches) {
        if (ctx.getAttribute("role") !== "button") {
          ctx.setAttribute("role", "button");
          ctx.setAttribute("aria-expanded", ctx.classList.contains("is-open") ? "true" : "false");
          ctx.setAttribute("tabindex", "0");
        }
      } else {
        ctx.removeAttribute("role");
        ctx.removeAttribute("aria-expanded");
        ctx.removeAttribute("tabindex");
        /* Make sure the desktop card is fully expanded (the CSS already
         * removes max-height on >600px, but clear .is-open for cleanliness). */
        ctx.classList.remove("is-open");
      }
    }
    applyMobileRole();
    /* Re-apply if the user resizes / rotates */
    if (mqMobile.addEventListener) mqMobile.addEventListener("change", applyMobileRole);
    else if (mqMobile.addListener) mqMobile.addListener(applyMobileRole);

    ctx.addEventListener("click", (e) => {
      if (!mqMobile.matches) return;
      if (e.target.closest("button, a, [role='radio']")) return;
      toggleCtx();
    });
    ctx.addEventListener("keydown", (e) => {
      if (!mqMobile.matches) return;
      if (e.target.closest("button, a, [role='radio']")) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleCtx();
      }
    });
  }

  /* ---------------------------------------------------------------------
   * 7d. Buy-options popup (mobile PDP). Intercepts the buy-bar CTA
   *     and shows a small bottom sheet with three options:
   *     Flipkart / Amazon / Find a store. Each one routes to the
   *     respective destination. Cancel button or backdrop dismisses.
   * ------------------------------------------------------------------- */
  (function setupBuyOptions() {
    if (!document.body.classList.contains("product")) return;
    if (!window.matchMedia("(max-width: 600px)").matches) return;
    const cta = document.querySelector(".buy-bar .cta");
    if (!cta || document.querySelector(".buy-options")) return;

    /* TODO: replace placeholder URLs with real partner deep-links
     * before launch. cta href is the canonical Flipkart fallback. */
    const flipkartHref = cta.getAttribute("href") || "https://www.flipkart.com/";
    const amazonHref   = "https://www.amazon.in/";
    const storesHref   = "/pages/stores";

    const popup = document.createElement("div");
    popup.className = "buy-options";
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "true");
    popup.setAttribute("aria-label", "Choose where to buy");
    popup.innerHTML = `
      <div class="buy-options__sheet" role="document">
        <p class="buy-options__title">Where to buy</p>
        <a class="buy-options__option" href="${flipkartHref}" target="_blank" rel="noopener">
          <span>Flipkart <span class="label-meta">·  Online</span></span>
          <span class="arrow" aria-hidden="true">→</span>
        </a>
        <a class="buy-options__option" href="${amazonHref}" target="_blank" rel="noopener">
          <span>Amazon India <span class="label-meta">·  Online</span></span>
          <span class="arrow" aria-hidden="true">→</span>
        </a>
        <a class="buy-options__option" href="${storesHref}">
          <span>Find a store <span class="label-meta">·  Near you</span></span>
          <span class="arrow" aria-hidden="true">→</span>
        </a>
        <button type="button" class="buy-options__cancel" aria-label="Cancel">Cancel</button>
      </div>
    `;
    document.body.appendChild(popup);

    function openOptions() { popup.classList.add("is-open"); }
    function closeOptions() { popup.classList.remove("is-open"); }

    cta.addEventListener("click", (e) => {
      e.preventDefault();
      openOptions();
    });
    popup.addEventListener("click", (e) => {
      /* Click on backdrop (the dialog itself, not the sheet) closes. */
      if (e.target === popup) closeOptions();
    });
    popup.querySelector(".buy-options__cancel").addEventListener("click", closeOptions);
    /* Tapping an option navigates — close the sheet first so the
     * back-button doesn't reopen it on return. */
    popup.querySelectorAll(".buy-options__option").forEach((o) => {
      o.addEventListener("click", () => closeOptions());
    });
    /* Esc closes */
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && popup.classList.contains("is-open")) closeOptions();
    });
  })();

  /* ---------------------------------------------------------------------
   * 7e. PDP two-pill focus stack — Spec front, Buy peeking back.
   *     Swipe up/down on the front pill OR tap the back pill to swap
   *     focus. Tap on the FRONT buy pill navigates to the partner site.
   *     Replaces the buy-bar, context-card, details pill, card-stack,
   *     and the earlier flip card on mobile PDPs.
   * ------------------------------------------------------------------- */
  if (
    document.body.classList.contains("product") &&
    window.matchMedia("(max-width: 600px)").matches &&
    ctx &&
    !document.querySelector(".pdp-pill-stack")
  ) {
    const buyBarEl = document.querySelector(".buy-bar");
    const name = (ctx.querySelector(".name")?.textContent || "").trim();
    const finishLabel = (
      ctx.querySelector("#variant-label")?.textContent ||
      ctx.querySelector(".bracket")?.textContent ||
      ""
    ).trim();
    const price = (buyBarEl?.querySelector(".price")?.textContent || "").trim();
    const meta = (buyBarEl?.querySelector(".meta")?.textContent || "").trim();
    const ctaEl = buyBarEl?.querySelector(".cta");
    const ctaText = (ctaEl?.textContent || "Buy on Flipkart").replace("→", "").trim();
    const ctaHref = ctaEl?.getAttribute("href") || "#";

    /* Pull up to 6 unique specs to feed the spec grid */
    const allSpecs = [...ctx.querySelectorAll(".spec")]
      .map((s) => ({
        k: (s.querySelector(".k")?.textContent || "").trim(),
        v: (s.querySelector(".v")?.textContent || "").trim()
      }))
      .filter((s) => s.k && s.v);
    const seen = new Set();
    const specs = allSpecs.filter((s) => (seen.has(s.k) ? false : (seen.add(s.k), true))).slice(0, 6);
    const specRowsHTML = specs
      .map((s) => `<span class="row"><span class="k">${s.k}</span><span class="v">${s.v}</span></span>`)
      .join("");

    /* Single one-line pill split into two independently-tappable halves:
     * left = "+ DETAILS" → opens the .context-card spec sheet.
     * right = "₹X BUY NOW →" → opens the .buy-options popup.
     * No focus stack, no swipe — both halves visible and reachable in
     * one tap. Pulse-Orange used only as accent (icon, price, arrow). */
    const stack = document.createElement("div");
    stack.className = "pdp-pill-stack";
    stack.setAttribute("aria-label", "Product details and buy options");
    stack.innerHTML = `
      <button type="button" class="pdp-pill-half pdp-pill--spec" aria-label="Open product details">
        <span class="pdp-pill-half__plus" aria-hidden="true">+</span>
        <span class="pdp-pill-half__label">Details</span>
      </button>
      <span class="pdp-pill-divider" aria-hidden="true"></span>
      <button type="button" class="pdp-pill-half pdp-pill--buy" aria-label="Open buy options">
        <span class="pdp-pill-half__price">${price}</span>
        <span class="pdp-pill-half__cta">Buy now <span aria-hidden="true">→</span></span>
      </button>
    `;
    document.body.appendChild(stack);

    const specPill = stack.querySelector(".pdp-pill--spec");
    const buyPill = stack.querySelector(".pdp-pill--buy");

    /* Tap left half → spec sheet. Tap right half → buy popup. */
    specPill.addEventListener("click", (e) => {
      e.preventDefault();
      ctx.classList.add("is-open");
      document.body.classList.add("ctx-open");
    });
    buyPill.addEventListener("click", (e) => {
      e.preventDefault();
      const popup = document.querySelector(".buy-options");
      if (popup) popup.classList.add("is-open");
    });

    /* Auto-hide on scroll-down, reveal on scroll-up — mirrors the
     * mobile-address-bar pattern (Nothing PDP, Twitter, Safari). The
     * pill always reveals when the user pauses or scrolls upward, so
     * Buy stays one tap away whenever they're considering. Hides only
     * when the user is actively scrolling down to read deeper content. */
    let lastScrollY = window.scrollY;
    let scrollIdleTimer = null;
    const SCROLL_DELTA = 8;             /* min px before reacting */
    const SCROLL_HIDE_AFTER = 120;      /* don't hide above this scroll */
    function onScroll() {
      const y = window.scrollY;
      const dy = y - lastScrollY;
      if (Math.abs(dy) < SCROLL_DELTA) return;
      if (y > SCROLL_HIDE_AFTER && dy > 0) {
        document.body.classList.add("pill-stowed");
      } else {
        document.body.classList.remove("pill-stowed");
      }
      lastScrollY = y;
      /* Reveal again if scroll pauses for 800ms */
      clearTimeout(scrollIdleTimer);
      scrollIdleTimer = setTimeout(() => {
        document.body.classList.remove("pill-stowed");
      }, 800);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
  }
  if (false) {
    const name = (ctx.querySelector(".name")?.textContent || "").trim();
    const description = (ctx.querySelector(".description")?.textContent || "").trim();
    const finishLabel = (
      ctx.querySelector("#variant-label")?.textContent ||
      ctx.querySelector(".bracket")?.textContent ||
      ""
    ).trim();
    const specs = [...ctx.querySelectorAll(".spec, .specs > .spec, .specs-toggle .spec, .specs-more .spec")]
      .map((s) => ({
        k: s.querySelector(".k")?.textContent?.trim() || "",
        v: s.querySelector(".v")?.textContent?.trim() || ""
      }))
      .filter((s) => s.k && s.v);

    /* Unique by key (in case the existing toggles duplicate them in DOM). */
    const seen = new Set();
    const uniqueSpecs = specs.filter((s) => {
      if (seen.has(s.k)) return false;
      seen.add(s.k);
      return true;
    });
    const top3 = uniqueSpecs.slice(0, 3);
    const rest = uniqueSpecs.slice(3);

    const cards = [
      {
        num: "01 / IDENTITY",
        h: name || "POVA",
        body: description || "Engineered, not styled."
      },
      {
        num: "02 / FINISH",
        h: finishLabel || "—",
        body: "The colourway you remember when the screen turns off."
      },
      {
        num: "03 / PERFORMANCE",
        h: top3.map((s) => `${s.v}`).join(" · ") || "Built to hold the day.",
        body: top3.map((s) => `${s.k} ${s.v}`).join("  ·  ")
      },
      {
        num: "04 / BUILD",
        h: rest.length ? "More inside." : "Engineered through.",
        specs: rest
      }
    ];

    const stack = document.createElement("div");
    stack.className = "pdp-card-stack";
    stack.setAttribute("aria-label", "Product story");
    stack.innerHTML =
      cards
        .map((c, i) => {
          const inner = c.specs && c.specs.length
            ? `<span class="card-num">${c.num}</span>
               <h3>${c.h}</h3>
               <div class="spec-grid">${c.specs.map((s) => `<span><span class="k">${s.k}</span><span class="v">${s.v}</span></span>`).join("")}</div>`
            : `<span class="card-num">${c.num}</span>
               <h3>${c.h}</h3>
               <p>${c.body}</p>`;
          return `<article class="pdp-card-stack__card" data-pos="${i}" tabindex="0" aria-label="${c.num} card">${inner}</article>`;
        })
        .join("") +
      `<div class="pdp-card-stack__dots" aria-hidden="true">${cards.map((_, i) => `<span class="${i === 0 ? "is-active" : ""}"></span>`).join("")}</div>`;
    document.body.appendChild(stack);

    const allCards = [...stack.querySelectorAll(".pdp-card-stack__card")];
    const dots = [...stack.querySelectorAll(".pdp-card-stack__dots span")];
    let order = allCards.map((_, i) => i);  // index of card at each position 0..3

    function render() {
      allCards.forEach((c, idx) => {
        const pos = order.indexOf(idx);
        c.setAttribute("data-pos", String(pos));
        c.style.transform = "";    // clear inline drag transform
      });
      dots.forEach((d, i) => d.classList.toggle("is-active", i === order[0]));
    }
    function advance() {
      order.push(order.shift());
      render();
    }

    /* Drag/swipe on the top card */
    let startX = 0, dragging = false, dx = 0;
    stack.addEventListener("pointerdown", (e) => {
      const top = allCards.find((c) => c.getAttribute("data-pos") === "0");
      if (!top || !top.contains(e.target) && top !== e.target) return;
      startX = e.clientX;
      dragging = true;
      top.setPointerCapture && top.setPointerCapture(e.pointerId);
    });
    stack.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      const top = allCards.find((c) => c.getAttribute("data-pos") === "0");
      dx = e.clientX - startX;
      top.style.transform = `translate(${dx}px, ${Math.abs(dx) * 0.05}px) rotate(${dx * 0.06}deg)`;
    });
    stack.addEventListener("pointerup", (e) => {
      if (!dragging) return;
      dragging = false;
      const top = allCards.find((c) => c.getAttribute("data-pos") === "0");
      if (Math.abs(dx) > 80) {
        /* Fling out, then advance. */
        top.classList.add("is-flying-out");
        top.style.transform = `translate(${dx > 0 ? 480 : -480}px, 60px) rotate(${dx > 0 ? 18 : -18}deg)`;
        setTimeout(() => {
          top.classList.remove("is-flying-out");
          advance();
        }, 320);
      } else {
        top.style.transform = "";  // snap back
      }
      dx = 0;
    });
    stack.addEventListener("pointercancel", () => {
      dragging = false;
      const top = allCards.find((c) => c.getAttribute("data-pos") === "0");
      if (top) top.style.transform = "";
    });
    /* Tap (without drag) advances the deck. */
    stack.addEventListener("click", (e) => {
      if (Math.abs(dx) > 4) return;
      const top = allCards.find((c) => c.getAttribute("data-pos") === "0");
      if (top && (e.target === top || top.contains(e.target))) advance();
    });
    /* Keyboard: arrow keys / Space / Enter on focused card */
    stack.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === " " || e.key === "Enter") {
        e.preventDefault();
        advance();
      }
    });
  }

  /* ---------------------------------------------------------------------
   * 7d. Constellation node activation (products.html). Hover/focus a node:
   *     its connecting edges (and only those) light orange. data-active
   *     on the root drives the CSS edge highlighting.
   * ------------------------------------------------------------------- */
  document.querySelectorAll(".constellation").forEach((root) => {
    const nodes = [...root.querySelectorAll(".node")];
    function activate(node) {
      nodes.forEach((n) => n.classList.toggle("is-active", n === node));
      if (node && node.dataset.pos) root.setAttribute("data-active", node.dataset.pos);
    }
    function deactivate() {
      nodes.forEach((n) => n.classList.remove("is-active"));
      root.removeAttribute("data-active");
    }
    nodes.forEach((node) => {
      node.addEventListener("mouseenter", () => activate(node));
      node.addEventListener("focus", () => activate(node));
      node.addEventListener("touchstart", () => activate(node), { passive: true });
    });
    root.addEventListener("mouseleave", deactivate);
    root.addEventListener("focusout", (e) => {
      if (!root.contains(e.relatedTarget)) deactivate();
    });
  });

  /* ---------------------------------------------------------------------
   * 8. Halo color on focus — keyboard users get the same product-accent
   *    halo shift hover users get. Listens for focusin on [data-product]
   *    elements and forwards to the field's engage() if loaded.
   * ------------------------------------------------------------------- */
  document.addEventListener("focusin", (e) => {
    const el = e.target.closest && e.target.closest("[data-product]");
    if (el && window.POVA_FIELD) window.POVA_FIELD.engage(el.dataset.product);
  });
  document.addEventListener("focusout", (e) => {
    const el = e.target.closest && e.target.closest("[data-product]");
    if (!el || !window.POVA_FIELD) return;
    if (e.relatedTarget && el.contains(e.relatedTarget)) return;
    /* If next focus is also a product, let its focusin handle. Otherwise
     * the field idles back to default. */
    const next = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest("[data-product]");
    if (!next) window.POVA_FIELD.disengage(el.dataset.product);
  });

  /* ---------------------------------------------------------------------
   * 10. Touch field halo idle — give the field faint presence on touch
   *     devices even before any input. field.js handles the actual touch
   *     position tracking via its own listeners.
   * ------------------------------------------------------------------- */
  if (isTouch) {
    /* Use a small delay so field.js's resetField() init runs first */
    setTimeout(() => {
      document.documentElement.style.setProperty("--field-halo-strength", "0.18");
    }, 80);
  }

  /* ---------------------------------------------------------------------
   * 10b. [data-reload] handler — replaces the wave-2 javascript: pseudo-URL
   *      pattern on 500.html. CSP-clean, a11y-clean.
   * ------------------------------------------------------------------- */
  document.querySelectorAll("[data-reload]").forEach((btn) => {
    btn.addEventListener("click", () => location.reload());
  });

  /* ---------------------------------------------------------------------
   * 11. View Transition API — handle <a> clicks within the same origin
   *     so we get the transition even when the browser doesn't auto-fire.
   * ------------------------------------------------------------------- */
  if (document.startViewTransition && !reduceMotion) {
    document.addEventListener("click", (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      if (a.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.hasAttribute("download")) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      /* Same-origin navigation — let the @view-transition CSS handle it.
       * (Browsers that support @view-transition: navigation: auto will
       * pick this up natively. We intentionally do not preventDefault. */
    });
  }

})();
